import { Command, Option } from 'commander';
import { getV2Client } from '../../api/v2-client.js';
import type { IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveSpaceIdBySlug } from '../space/show.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';

const ROLES = ['admin', 'developer', 'viewer', 'publisher'] as const;
type Role = typeof ROLES[number];

interface InviteOpts extends GridOpts {
  role?: string;
  space?: string[];
  note?: string;
  json?: boolean;
}

interface InviteBody {
  emails: string[];
  role: string;
  space_ids?: string[];
  note?: string;
}

interface InviteResp {
  invitations: Array<{ invitation_id: string; invited_email: string; role: Role; expires_at: string }>;
  failures: Array<{ email: string; code: string; message: string }>;
}

export async function runAdminInvite(emailsArg: string, opts: InviteOpts, client?: IV2Client): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const emails = emailsArg.split(',').map((e) => e.trim()).filter(Boolean);
  if (emails.length === 0) throw new V2ApiError(400, 'VALIDATION_FAILED', 'Provide at least one email.');
  const c = client ?? await getV2Client(gridSlug(opts));
  const space_ids: string[] = [];
  for (const slug of opts.space ?? []) space_ids.push(await resolveSpaceIdBySlug(c, slug));
  const body: InviteBody = { emails, role: opts.role ?? 'developer' };
  if (space_ids.length > 0) body.space_ids = space_ids;
  if (opts.note) body.note = opts.note;
  const resp = await c.post<InviteResp>('/api/v2/invitations', body);
  out.json(() => resp);
  out.human(() => {
    const lines: string[] = [];
    if (resp.invitations.length > 0) {
      const n = resp.invitations.length;
      lines.push(`✓ Sent ${n} invitation${n === 1 ? '' : 's'}.`);
      for (const inv of resp.invitations) {
        lines.push(`  - ${inv.invited_email} — ${inv.role} — expires ${inv.expires_at}`);
      }
    }
    if (resp.failures.length > 0) {
      lines.push('');
      lines.push(`Failed to invite:`);
      for (const f of resp.failures) lines.push(`  - ${f.email}: ${f.code} — ${f.message}`);
    }
    return lines.join('\n');
  });
}

export const adminInviteCommand = new Command('invite')
  .description('Invite one or more emails to the active grid (admin only)')
  .argument('<emails>', 'Single email or comma-separated list')
  .addOption(new Option('--role <role>', 'Role for invitee(s)').choices(ROLES as unknown as string[]).default('developer'))
  .option('--space <slug>', 'Space to pre-assign (repeatable)', (v: string, acc: string[] = []) => [...acc, v])
  .option('--note <text>', 'Note included in the invitation email (≤500 chars)')
  .option('--json', 'JSON output');
addGridOption(adminInviteCommand, 'Override active grid');
adminInviteCommand
  .action(async (emailsArg: string, opts: InviteOpts) => {
    try { await runAdminInvite(emailsArg, opts); }
    catch (err) { await handleV2Error(err, { json: !!opts.json }); }
  });
